import { find, merge } from 'lodash';

import { getOperatorCurrencyRates } from 'sletat-api-services/lib/currency/getOperatorCurrencyRates';
import { OperatorCurrencyItem } from 'sletat-api-services/lib/currency/getOperatorCurrencyRates/response';
import { getBankCurrencyRates } from 'sletat-api-services/lib/sletat/getBankCurrencyRates';

import { CurrencyId } from '../models/actualization';
import { hotelPageLogger } from '../../utils/logger';
import { CURRENCY_HOST_NAME, SLETAT_HOST_NAME_PUBLIC } from '../../consts/hosts';


export interface Currencies {
    [key: number]: {
        [key: number]: {
            bank?: number;
            operator?: number;
        };
    };
}


export type RateKeys = 'bank' | 'operator';


export function getCurrencyRates(sourceId: number, requestId: number): Promise<Currencies> {
    return Promise.all([
        getOperatorCurrencyRates({ host: CURRENCY_HOST_NAME })({ operatorId: sourceId }),
        getBankCurrencyRates({ host: SLETAT_HOST_NAME_PUBLIC })({ requestId, sourceId })
    ])
        .then(([operatorRates, bankRates]) => {
            return merge(
                {},
                makeRatesFromCourses(bankRates, 'bank'),
                makeRatesFromCourses(normalizeOperatorCourses(operatorRates), 'operator')
            );
        })
        .catch(err => {
            hotelPageLogger.warn('getCurrencyRates failed: ', err);
            throw err;
        });
}

/**
 * TODO selkin: вся нижележащая логика должна быть в sletat-api-services
 */
export interface Course {
    count: number;
    price: number;
    id: CurrencyId;
}

export class CurrencyStore {
    currencies: Currencies | null;

    constructor(currencies: Currencies | null) {
        this.currencies = currencies;
    }

    convertCurrency(amount: number, currencyIdFrom: CurrencyId, currencyIdTo: CurrencyId): number {
        if (!this.currencies) {
            return amount;
        }
        const type: RateKeys = this.isOperatorRate(currencyIdFrom, currencyIdTo) ? 'operator' : 'bank';
        const rate = this.currencies[currencyIdFrom][currencyIdTo][type] || 1;
        return amount * rate;
    }

    isOperatorRate(currencyIdFrom: CurrencyId, currencyIdTo: CurrencyId): boolean {
        if (!this.currencies) {
            return false;
        }
        return !!this.currencies[currencyIdFrom][currencyIdTo].operator;
    }

    isEmpty(): boolean {
        return this.currencies === null;
    }
}

/**
 * Делаем новый объект с курсами валют, где есть отношение каждой валюты к каждой
 */
function makeRatesFromCourses(courses: Array<Course>, key: RateKeys): Currencies {
    const rates: Currencies = {};
    /**
     * Получаем список идентификаторов всех валют
     */
    const ids = courses.map((course: Course) => course.id);
    /**
     * Для каждой валюты получаем отношения всех других валют
     */
    ids.forEach((id: CurrencyId) => {
        if (!rates[id]) {
            rates[id] = {};
        }
        const currentCurrency = rates[id];
        courses.map((course: Course) => {
            if (!currentCurrency[course.id]) {
                currentCurrency[course.id] = {};
            }
            const currentCourse = currentCurrency[course.id];
            currentCourse[key] = convertCurrencyInternal(id, course.id, courses);
        });
    });
    return rates;
}

function normalizeOperatorCourses(courses: Array<OperatorCurrencyItem>): Array<Course> {
    /**
     * Сервис отдает не только отношение рублей к какой-либо валюте,
     * но иногда наоборот, отношение некой валюты к рублям, например
     * беллоруские рубли приходят в таком обратном виде, мы это убираем,
     * делаем единообразно -> рубли к другим валютам
     * Затем приводим их к более удобному нам виду type=Course
     */
    const normalizedCourses = courses.map((course: OperatorCurrencyItem) => {
        if (course.baseCurrency !== 5 && course.relativeCurrency === 5) {
            course.relativeCurrency = course.baseCurrency;
            course.baseCurrency = 5;
            course.rate = 1 / course.rate;
        }
        return course;
    }).map((course: OperatorCurrencyItem) => {
        return {
            count: 1,
            price: course.rate,
            id: course.relativeCurrency
        };
    });
    /**
     * Опять же для единообразия с сервисом валют банка добавляем сюда сами рубли
     */
    normalizedCourses.push({
        count: 1,
        price: 1,
        id: 5
    });

    return normalizedCourses;
}

/**
 * Конвертируем валюты, сначала приводим к рублям, т.к. изначально мы имеем
 * только отнешения рубля к чему-либо
 */
function convertCurrencyInternal(currencyIdFrom: CurrencyId, currencyIdTo: CurrencyId, courses: Array<Course>) {
    const fromCourse = find(courses, (course: Course) => {
        return course.id === currencyIdFrom;
    });
    const priceInRoubles = fromCourse!.price / fromCourse!.count;

    const toCourse = find(courses, (course: Course) => {
        return course.id === currencyIdTo;
    });

    return priceInRoubles / toCourse!.price * toCourse!.count;
}
